import type { ResultPayload, TypeResult } from "@mesketch/engine";
import { adjectiveKey, capitalize, nounKey, typeset } from "@mesketch/i18n";
import { PencilScale, ProgressBar, TypeDrawing, type DrawingStage } from "@mesketch/svg";
import type { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { FeedbackValue, ResultView } from "../data/api.js";
import { drawingOf, isPlaceholderDrawing } from "./content.js";
import { fill, T, useTexts } from "./texts.js";
import { useTone } from "./tone.js";

// Části mezivýsledku a Skici (REQ-022, REQ-023, DES-003 §6). Vše z payloadu a textů, nic se
// nepřepočítává. Skóre ani percentil se nikdy neukazuje jako číslo (CON-002); procenta jsou jen
// podíly simulací typu.

type WordState = TypeResult["noun"];

/** Podíl jako procento podle zvyklostí jazyka („71 %“, „71%“). */
export function percent(share: number, locale: string): string {
  return new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 0 }).format(share);
}

function Word({ state, children }: { state: WordState["state"]; children: ReactNode }) {
  if (state === "race") {
    return (
      <span className="word word--race" data-state="race">
        <svg className="word__dots" viewBox="0 0 30 6" aria-hidden="true">
          <circle cx="3" cy="3" r="2.2" fill="currentColor" />
          <circle cx="15" cy="3" r="2.2" fill="currentColor" />
          <circle cx="27" cy="3" r="2.2" fill="currentColor" />
        </svg>
        <span className="visually-hidden">
          <FormattedMessage id="round.word.drawing" />
        </span>
      </span>
    );
  }
  return (
    <span className={`word word--${state}`} data-state={state}>
      {children}
    </span>
  );
}

/**
 * Přídavné jméno v rodě podstatného jména. Bez textu rodu v náhledu ukáže označený chybějící
 * klíč rodu (REQ-031), v sezení uživatele nic.
 */
export function AdjectiveWord({ noun, adjective, cap = false }: { noun: string; adjective: string; cap?: boolean }) {
  const { texts } = useTexts();
  const k = adjectiveKey(texts, noun, adjective);
  return <T k={k ?? `type.noun_gender.${noun}`} cap={cap} />;
}

/** Jméno typu: přídavné a podstatné jméno, první písmeno velké. */
export function TypeNameWords({ noun, adjective }: { noun: string; adjective: string }) {
  return (
    <>
      <AdjectiveWord noun={noun} adjective={adjective} cap /> <T k={nounKey(noun)} />
    </>
  );
}

/** Slova typu podle stavů `race`, `pencil`, `ink` (REQ-007, DES-003 §6). */
export function TypeWords({ type }: { type: TypeResult }) {
  const noun = type.noun.value;
  const adj = type.adjective.value;
  return (
    <div className="type-words" data-testid="type-words">
      <Word state={noun === null || adj === null ? "race" : type.adjective.state}>{noun === null || adj === null ? null : <AdjectiveWord noun={noun} adjective={adj} />}</Word>{" "}
      <Word state={noun === null ? "race" : type.noun.state}>{noun === null ? null : <T k={nounKey(noun)} />}</Word>
    </div>
  );
}

export function drawingStage(type: TypeResult, final: boolean): DrawingStage {
  if (final) return "color";
  if (type.noun.state === "race") return "ghost";
  return type.noun.state;
}

export function TypeSketch({ type, view, final, label }: { type: TypeResult; view: ResultView; final: boolean; label: string }) {
  const noun = final ? (type.final?.noun ?? null) : type.noun.value;
  const primary = noun ?? type.nouns[0]?.[0] ?? "mosaic";
  const secondary = type.noun.state === "race" && !final ? (type.nouns[1]?.[0] ?? null) : null;
  const detail = type.detail === null ? null : final || type.adjective.state === "ink" ? "ink" : "pencil";
  // Zástupné kresby se neukazují (Libor 22. 10. 2026, REQ-023), druhá jen skutečná.
  const primaryId = drawingOf(primary, view.domains);
  const secondaryId = secondary === null ? null : drawingOf(secondary, view.domains);
  if (isPlaceholderDrawing(primaryId)) return null;
  return (
    <div className="drawing" data-testid="type-drawing">
      <TypeDrawing
        primary={primaryId}
        secondary={secondaryId === null || isPlaceholderDrawing(secondaryId) ? null : secondaryId}
        stage={drawingStage(type, final)}
        detail={detail}
        label={label}
      />
    </div>
  );
}

function RaceRow({ name, label, share, locale }: { name: ReactNode; label: string; share: number; locale: string }) {
  return (
    <div className="race-row">
      <span className="race-row__name">{name}</span>
      <span className="race-row__bar">
        <ProgressBar value={share} label={label} tone={useTone()} />
      </span>
      <span className="race-row__share">{percent(share, locale)}</span>
    </div>
  );
}

/** Závod kandidátů: podstatná jména, pak přídavná jména, pak jistota obou (typ-hexaco). */
export function RaceCard({ type, view, round }: { type: TypeResult; view: ResultView; round: number }) {
  const { texts, locale, text } = useTexts();
  const n = view.simulations ?? type.nouns.reduce((a, [, c]) => a + c, 0);
  const shown = view.candidatesShown ?? 3;
  const topNoun = type.nouns[0];
  if (n === 0 || topNoun === undefined) return null;
  const nounRow = (w: string, share: number) => ({ id: w, name: <T k={nounKey(w)} cap />, label: text(nounKey(w)) ?? w, share });
  const adjRow = (a: string, share: number) => {
    const k = adjectiveKey(texts, topNoun[0], a);
    return { id: a, name: <AdjectiveWord noun={topNoun[0]} adjective={a} cap />, label: (k === null ? undefined : text(k)) ?? a, share };
  };
  let title: string;
  let rows: { id: string; name: ReactNode; label: string; share: number }[];
  if (type.noun.state === "race") {
    title = "round.race.noun";
    rows = type.nouns.slice(0, shown).map(([w, c]) => nounRow(w, c / n));
  } else if (type.adjective.state === "race") {
    title = "round.race.adjective";
    rows = type.adjectives.slice(0, shown).map(([a, c]) => adjRow(a, c / topNoun[1]));
  } else {
    title = "round.certainty";
    const topAdj = type.adjectives[0];
    rows = [nounRow(topNoun[0], topNoun[1] / n)];
    if (topAdj !== undefined) rows.push(adjRow(topAdj[0], topAdj[1] / topNoun[1]));
  }
  return (
    <div className="card" data-testid="race">
      <p className="label">
        <FormattedMessage id={title} values={{ round }} />
      </p>
      {rows.map((r) => (
        <RaceRow key={r.id} name={r.name} label={capitalize(r.label, locale)} share={r.share} locale={locale} />
      ))}
    </div>
  );
}

const STATE_IDS = { clear: "state.clear", almost: "state.almost", drawing: "state.drawing" } as const;

export interface DomainFeedback {
  readonly value: FeedbackValue | null;
  readonly onChange: (v: FeedbackValue) => void;
}

/** Domény mezi koly jako škály tužkou s póly a štítkem stavu (REQ-022, CR-014 §4). */
export function DomainBars({
  payload,
  view,
  final,
  texts: withTexts,
  feedback,
}: {
  payload: ResultPayload;
  view: ResultView;
  final: boolean;
  texts?: "free" | null;
  feedback?: (domain: string) => DomainFeedback | null;
}) {
  const intl = useIntl();
  const { text } = useTexts();
  const tone = useTone();
  return (
    <div data-testid="domains">
      {view.domains.map((d) => {
        const s = payload.scales[d];
        if (s === undefined) return null;
        const name = text(`scale.${d}.name`) ?? d;
        const low = text(`poles.${d}-`) ?? `${d}-`;
        const high = text(`poles.${d}+`) ?? `${d}+`;
        const stateId = s.state === null ? null : STATE_IDS[s.state];
        const label = final || stateId === null ? intl.formatMessage({ id: "bar.labelFinal" }, { scale: name, low, high }) : intl.formatMessage({ id: "bar.label" }, { scale: name, low, high, state: intl.formatMessage({ id: stateId }) });
        const fb = feedback?.(d) ?? null;
        const hinted = view.lowReliability?.includes(d) === true;
        return (
          <div className="domain" key={d} data-domain={d}>
            <div className="domain__head">
              <span className="domain__name">
                <T k={`scale.${d}.name`} />
              </span>
              {final ? (
                s.band === null ? null : (
                  <span className="chip chip--clear" data-testid="band">
                    <T k={s.pole === null ? `bands.domain.${s.band}.name` : `poles.${s.pole}`} />
                  </span>
                )
              ) : stateId === null ? null : (
                <span className={`chip chip--${s.state}`}>
                  <FormattedMessage id={stateId} />
                </span>
              )}
              {hinted ? (
                <span className="chip chip--hint" data-testid="hint">
                  <FormattedMessage id="sketch.hint" />
                </span>
              ) : null}
            </div>
            <PencilScale z={s.z} ci={s.ci} neutral={view.neutral} state={final ? null : s.state} lowReliability={hinted} size="lg" tone={tone} label={label} />
            <div className="domain__poles">
              <span>
                <T k={`poles.${d}-`} />
              </span>
              <span>
                <T k={`poles.${d}+`} />
              </span>
            </div>
            {withTexts === "free" && s.band !== null ? (
              <p className="small domain__text">
                <T k={`bands.domain.${d}.${s.band}.free`} />
              </p>
            ) : null}
            {fb === null ? null : <Fits value={fb.value} onChange={fb.onChange} />}
          </div>
        );
      })}
    </div>
  );
}

const FIT_VALUES: readonly FeedbackValue[] = ["fits", "partly", "doesnt_fit"];

/** „Sedí to na mě?“ (REQ-025): tři volby, jen majitel. */
export function Fits({ value, onChange }: DomainFeedback) {
  return (
    <div className="fits" role="group">
      <span>
        <FormattedMessage id="sketch.fits.question" />
      </span>
      {FIT_VALUES.map((v) => (
        <button key={v} type="button" aria-pressed={value === v} onClick={() => onChange(v)} data-value={v}>
          <FormattedMessage id={`sketch.fits.${v}`} />
        </button>
      ))}
    </div>
  );
}

/** Teaser další otázky (REQ-010): parametry jsou slova typu a seznam domén. */
export function Teaser({ payload }: { payload: ResultPayload }) {
  const { texts, text, locale, preview } = useTexts();
  const t = payload.teaser;
  const template = t === null ? undefined : texts[t.key];
  if (t === null || template === undefined) return t === null ? null : <p className="teaser"><T k={t.key} /></p>;
  const topNoun = payload.type?.nouns[0]?.[0] ?? null;
  const params: Record<string, string> = {};
  const lower = (s: string) => s.slice(0, 1).toLocaleLowerCase(locale) + s.slice(1);
  for (const [name, v] of Object.entries(t.params)) {
    if (v === null || v === undefined) continue;
    // Seznam domén uprostřed věty malým písmenem („Ještě se kreslí: extraverze, otevřenost.“).
    if (Array.isArray(v)) params[name] = v.map((s) => lower(text(`scale.${s}.name`) ?? s)).join(", ");
    else if (name.startsWith("noun")) params[name] = text(`type.noun_case7.${v}`) ?? v;
    else {
      const k = topNoun === null ? null : adjectiveKey(texts, topNoun, v);
      params[name] = (k === null ? undefined : text(k)) ?? v;
    }
  }
  const value = fill(capitalize(typeset(template.value, locale), locale), params);
  return (
    <p className="teaser" data-testid="teaser">
      {preview && template.status !== "approved" ? (
        <mark className="mk-draft" data-status={template.status} data-key={t.key}>
          {value}
        </mark>
      ) : (
        value
      )}
    </p>
  );
}

export { typeName } from "./type-name.js";
